/**
 * Header - Feste Kopfzeile (WinValue-Style)
 * DESIGN: Professionell, reduziert. Koop-Dunkelrot als Akzent.
 *
 * Layout:
 * - Links: Hamburger-Icon zum Ein-/Ausblenden des Hauptmenues
 * - Mitte/Links: Logo "Gutachter Koop" + "Verwaltungssystem"
 * - Rechts: + Neue Akte, E-Mail-Freigaben, Glocke, Kiosk, Werkstatt, Status, Benutzer-Avatar, Logout
 */

import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useChat';
import { useBenachrichtigungen, prio as prioCss, typLabel, prioSortOrder } from '@/hooks/useBenachrichtigungen';
import { useLocation } from 'wouter';
import {
  Menu, Bell, Wrench, LogOut, ChevronDown,
  User, X, Building2, Plus, FileText,
  Car, Glasses, Truck, Hammer, Calendar, Eye, EyeOff, Users2,
  MessageSquare, CheckSquare, Trash2, Clock, AlertCircle, LayoutDashboard, Bot,
  ShieldAlert, Timer, ClipboardCheck, CreditCard, Scissors, FileX,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { BETRIEBE, type BetriebId } from '@/lib/betriebe';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
// TagesmanagerPopup ersetzt durch AgentenKommandozentrale
import AgentenKommandozentrale from '@/components/AgentenKommandozentrale';
import { filterKiAgent, isKiAgent } from '@/lib/kiAgent';
import FreigabenButton from '@/components/FreigabenButton';
import FonioAnrufButton from '@/components/FonioAnrufButton';
import NeuerTerminButton from '@/components/NeuerTerminButton';
import MitarbeiterMenu from '@/components/MitarbeiterMenu';

// Notifications werden aus Supabase geladen (kein Demo-Daten)

// --- Neue Akte Dialog ---
type NeueAkteType = 'gutachten' | 'autoglas' | 'mietwagen' | 'werkstatt';

interface NeueAkteDialogProps {
  type: NeueAkteType;
  onClose: () => void;
  onSuccess: (akteId: string) => void;
}

const AKTE_CONFIG: Record<NeueAkteType, {
  label: string;
  icon: React.ElementType;
  color: string;
  prefix: string;
  fields: { key: string; label: string; placeholder: string; required?: boolean; type?: string }[];
}> = {
  gutachten: {
    label: 'Gutachten-Akte',
    icon: FileText,
    color: '#1D4ED8',
    prefix: 'GA',
    fields: [
      { key: 'kundenname', label: 'Kundenname', placeholder: 'Mueller, Thomas', required: true },
      { key: 'kennzeichen', label: 'Kennzeichen', placeholder: 'HA-AB 1234', required: true },
      { key: 'fahrzeug', label: 'Fahrzeug', placeholder: 'VW Golf 7, 2019' },
      { key: 'schadendatum', label: 'Schadendatum', placeholder: '', type: 'date' },
      { key: 'beschreibung', label: 'Schadensbeschreibung', placeholder: 'Kurze Beschreibung des Schadens...' },
    ],
  },
  autoglas: {
    label: 'Autoglas-Akte',
    icon: Glasses,
    color: '#059669',
    prefix: 'AG',
    fields: [
      { key: 'kundenname', label: 'Kundenname', placeholder: 'Schulz, Maria', required: true },
      { key: 'kennzeichen', label: 'Kennzeichen', placeholder: 'HA-CD 5678', required: true },
      { key: 'fahrzeug', label: 'Fahrzeug', placeholder: 'BMW 3er, 2021' },
      { key: 'glastyp', label: 'Glastyp', placeholder: 'Frontscheibe / Seitenscheibe / Heckscheibe' },
      { key: 'beschreibung', label: 'Schadensbeschreibung', placeholder: 'Steinschlag, Riss, etc.' },
    ],
  },
  mietwagen: {
    label: 'Mietwagen-Akte',
    icon: Car,
    color: '#D97706',
    prefix: 'MW',
    fields: [
      { key: 'kundenname', label: 'Kundenname', placeholder: 'Weber, Klaus', required: true },
      { key: 'kennzeichen', label: 'Kennzeichen Unfallfahrzeug', placeholder: 'HA-EF 9012', required: true },
      { key: 'fahrzeug', label: 'Unfallfahrzeug', placeholder: 'Mercedes C-Klasse, 2020' },
      { key: 'mietbeginn', label: 'Mietbeginn', placeholder: '', type: 'date' },
      { key: 'beschreibung', label: 'Notizen', placeholder: 'Besondere Anforderungen...' },
    ],
  },
  werkstatt: {
    label: 'Werkstatt-Fall',
    icon: Hammer,
    color: '#7C3AED',
    prefix: 'WK',
    fields: [
      { key: 'kundenname', label: 'Kundenname', placeholder: 'Fischer, Anna', required: true },
      { key: 'kennzeichen', label: 'Kennzeichen', placeholder: 'HA-GH 3456', required: true },
      { key: 'fahrzeug', label: 'Fahrzeug', placeholder: 'Audi A4, 2018' },
      { key: 'reparatur', label: 'Reparaturart', placeholder: 'Karosserie / Motor / Elektronik / HU/AU' },
      { key: 'beschreibung', label: 'Auftragsbeschreibung', placeholder: 'Beschreibung des Auftrags...' },
    ],
  },
};

function NeueAkteDialog({ type, onClose, onSuccess }: NeueAkteDialogProps) {
  const config = AKTE_CONFIG[type];
  const Icon = config.icon;
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const required = config.fields.filter(f => f.required);
    for (const f of required) {
      if (!formData[f.key]?.trim()) {
        toast.error(`${f.label} ist ein Pflichtfeld`);
        return;
      }
    }

    setSaving(true);
    try {
      const year = new Date().getFullYear();
      const rand = Math.floor(Math.random() * 9000) + 1000;
      const akteNummer = `${config.prefix}-${year}-${rand}`;

      const insertData: Record<string, string | null> = {
        akte_nummer: akteNummer,
        betrieb: type,
        status: 'neu',
        kundenname: formData['kundenname'] || null,
        kennzeichen: formData['kennzeichen'] || null,
        fahrzeug: formData['fahrzeug'] || null,
        beschreibung: formData['beschreibung'] || formData['reparatur'] || formData['glastyp'] || null,
        schadendatum: formData['schadendatum'] || formData['mietbeginn'] || null,
      };

      const { data, error } = await supabase
        .from('akten')
        .insert(insertData)
        .select('id, akte_nummer')
        .single();

      if (error) {
        console.error('Supabase insert failed:', error.message);
        toast.error(`Fehler beim Anlegen: ${error.message}`);
        setSaving(false);
        return;
      } else {
        toast.success(`${config.label} ${data.akte_nummer} angelegt`, {
          description: `${formData['kundenname']} - ${formData['kennzeichen']}`,
        });
        onSuccess(data.id);
      }
    } catch (err) {
      toast.error('Fehler beim Anlegen der Akte');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${config.color}15` }}
          >
            <Icon className="w-5 h-5" style={{ color: config.color }} />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-gray-900">Neue {config.label}</h2>
            <p className="text-xs text-gray-400">Pflichtfelder sind mit * markiert</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {config.fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {field.label}{field.required && <span className="text-[#C41E3A] ml-0.5">*</span>}
              </label>
              {field.key === 'beschreibung' || field.key === 'reparatur' ? (
                <textarea
                  value={formData[field.key] || ''}
                  onChange={e => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#C41E3A]/50 focus:ring-2 focus:ring-[#C41E3A]/10 resize-none transition-colors"
                />
              ) : (
                <input
                  type={field.type || 'text'}
                  value={formData[field.key] || ''}
                  onChange={e => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#C41E3A]/50 focus:ring-2 focus:ring-[#C41E3A]/10 transition-colors"
                />
              )}
            </div>
          ))}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: config.color }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Anlegen...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Akte anlegen
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Neue Akte Dropdown ---
const NEUE_AKTE_OPTIONS: { type: NeueAkteType; label: string; desc: string; icon: React.ElementType; color: string }[] = [
  { type: 'gutachten', label: 'Gutachten', desc: 'Kfz-Gutachten anlegen', icon: FileText, color: '#1D4ED8' },
  { type: 'autoglas', label: 'Autoglas', desc: 'Glasschaden-Akte anlegen', icon: Glasses, color: '#059669' },
  { type: 'mietwagen', label: 'Mietwagen', desc: 'Mietfahrzeug-Akte anlegen', icon: Car, color: '#D97706' },
  { type: 'werkstatt', label: 'Werkstatt', desc: 'Werkstatt-Auftrag anlegen', icon: Hammer, color: '#7C3AED' },
];

export default function Header() {
  const { toggleSidebar, sidebarCollapsed, closeAkte } = useApp();
  const { profile, signOut, isAdmin, teamMembers, impersonateUserId, setImpersonateUserId } = useAuth();
  const notif = useNotifications();
  const bnach = useBenachrichtigungen();

  const impersonatedMember = impersonateUserId ? teamMembers.find(m => m.id === impersonateUserId) : null;
  const effectiveIsAdmin = impersonateUserId ? (impersonatedMember?.rolle === 'admin') : isAdmin;
  const effectiveProfile = impersonateUserId && impersonatedMember ? {
    name: `${impersonatedMember.vorname} ${impersonatedMember.nachname}`,
    rolle: impersonatedMember.rolle,
    initialen: `${impersonatedMember.vorname?.[0] || ''}${impersonatedMember.nachname?.[0] || ''}`.toUpperCase(),
  } : profile;
  const [location, setLocation] = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNeueAkte, setShowNeueAkte] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [activeAkteType, setActiveAkteType] = useState<NeueAkteType | null>(null);
  const [showAgentenZentrale, setShowAgentenZentrale] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const neueAkteRef = useRef<HTMLDivElement>(null);
  const impersonateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowNotifications(false);
    setShowUserMenu(false);
    setShowNeueAkte(false);
    setShowImpersonate(false);
  }, [location]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
      if (neueAkteRef.current && !neueAkteRef.current.contains(e.target as Node)) {
        setShowNeueAkte(false);
      }
      if (impersonateRef.current && !impersonateRef.current.contains(e.target as Node)) {
        setShowImpersonate(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleAkteSuccess = (akteId: string) => {
    setActiveAkteType(null);
    if (activeAkteType) {
      setLocation(`/${activeAkteType}`);
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-gray-200 flex items-center px-2 sm:px-3 lg:px-4 gap-1 sm:gap-2">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#9B1C1C]" />

        <button
          onClick={toggleSidebar}
          className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
          title={sidebarCollapsed ? 'Menue einblenden' : 'Menue ausblenden'}
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-[#9B1C1C] flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="text-sm font-bold text-gray-900">Gutachter Koop</span>
            <span className="text-[10px] text-gray-400 font-medium">Verwaltungssystem</span>
          </div>
        </div>

        {impersonateUserId && impersonatedMember && (
          <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-lg mx-2">
            <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-white">
                {impersonatedMember.vorname?.[0]}{impersonatedMember.nachname?.[0]}
              </span>
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-xs font-semibold text-amber-800">
                {impersonatedMember.vorname} {impersonatedMember.nachname}
              </span>
              <span className="text-[9px] text-amber-600 capitalize">{impersonatedMember.rolle}</span>
            </div>
            <button
              onClick={() => { setImpersonateUserId(null); toast.success('Zurueck zur Admin-Ansicht'); }}
              className="ml-1 w-5 h-5 flex items-center justify-center rounded-full hover:bg-amber-200 transition-colors"
              title="Ansicht beenden"
            >
              <X className="w-3 h-3 text-amber-700" />
            </button>
          </div>
        )}

        <div className="flex-1" />

        <div className="flex items-center gap-1">

          {effectiveIsAdmin && <div ref={neueAkteRef} className="relative">
            <button
              onClick={() => { setShowNeueAkte(!showNeueAkte); setShowNotifications(false); setShowUserMenu(false); }}
              className="flex items-center gap-1.5 bg-[#C41E3A] text-white text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-[#A3172E] transition-colors min-h-[36px]"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Neue Akte</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showNeueAkte ? 'rotate-180' : ''}`} />
            </button>

            {showNeueAkte && (
              <div className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] sm:w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Neue Akte anlegen</p>
                </div>
                <div className="py-1.5">
                  {NEUE_AKTE_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.type}
                        onClick={() => {
                          setShowNeueAkte(false);
                          setActiveAkteType(opt.type);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left min-h-[52px]"
                      >
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${opt.color}15` }}
                        >
                          <Icon className="w-4.5 h-4.5" style={{ color: opt.color }} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-800">{opt.label}</div>
                          <div className="text-[11px] text-gray-400">{opt.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>}

          <NeuerTerminButton />

          <FreigabenButton />

          <div className="w-px h-6 bg-gray-200 mx-1 hidden md:block" />

          {/* Eingehende Anrufe (fonio) */}
          <FonioAnrufButton />

          <div ref={notifRef} className="relative">
            <button
              onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); setShowNeueAkte(false); }}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors relative"
              title="Benachrichtigungen"
            >
              <Bell className="w-[18px] h-[18px] text-gray-600" />
              {(notif.unreadCount + bnach.unreadCount) > 0 && (
                <span className={`absolute top-1 right-1 w-4 h-4 text-white text-[9px] font-bold rounded-full flex items-center justify-center ${bnach.hasCritical ? 'bg-red-500' : 'bg-[#9B1C1C]'}`}>
                  {(notif.unreadCount + bnach.unreadCount) > 9 ? '9+' : (notif.unreadCount + bnach.unreadCount)}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] sm:w-96 max-w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Benachrichtigungen</span>
                    <p className="text-[10px] text-gray-400">
                      {(notif.unreadCount + bnach.unreadCount) > 0
                        ? `${notif.unreadCount + bnach.unreadCount} ungelesen`
                        : 'Alles gelesen'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(notif.unreadCount + bnach.unreadCount) > 0 && (
                      <button
                        onClick={() => { notif.markAllAsRead(); bnach.markAllAsRead(); }}
                        className="text-[10px] font-medium text-[#9B1C1C] hover:underline px-1.5 py-0.5 rounded hover:bg-red-50"
                      >
                        Alle gelesen
                      </button>
                    )}
                    {notif.notifications.length > 0 && (
                      <button
                        onClick={() => { if (window.confirm('Alle Nachrichten loeschen?')) notif.deleteAll(); }}
                        className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-gray-100 transition-colors"
                        title="Nachrichten loeschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="max-h-[28rem] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                  {bnach.sichtbar.length > 0 && (
                    <div>
                      <div className="px-4 pt-2.5 pb-1">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Wichtige Hinweise</p>
                      </div>
                      {[...bnach.sichtbar]
                        .sort((a, b) => prioSortOrder(b.prio) - prioSortOrder(a.prio))
                        .slice(0, 8)
                        .map(b => {
                          const css = prioCss(b.prio);
                          const BIcon = b.typ.startsWith('verjaehrung') ? Timer
                            : b.typ.startsWith('stellungnahme') ? ShieldAlert
                            : b.typ.startsWith('qs') ? ClipboardCheck
                            : b.typ === 'kuerzung_eingegangen' ? Scissors
                            : b.typ === 'zahlung_teilweise' ? CreditCard
                            : FileX;
                          const diffMin = Math.floor((Date.now() - new Date(b.erstellt_am).getTime()) / 60000);
                          const timeAgo = diffMin < 1 ? 'Gerade eben'
                            : diffMin < 60 ? `vor ${diffMin} Min`
                            : diffMin < 1440 ? `vor ${Math.floor(diffMin / 60)} Std`
                            : `vor ${Math.floor(diffMin / 1440)} Tagen`;
                          return (
                            <div
                              key={b.id}
                              className={`flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 group/bn cursor-pointer border-l-2 ${css.border}`}
                              onClick={() => {
                                bnach.markAsRead(b.id);
                                if (b.aktion_url) { setLocation(b.aktion_url); setShowNotifications(false); }
                              }}
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${css.badge}`}>
                                <BIcon className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded ${css.badge}`}>
                                    {typLabel(b.typ)}
                                  </span>
                                  <span className="text-[10px] text-gray-300 flex items-center gap-0.5">
                                    <Clock className="w-2.5 h-2.5" />{timeAgo}
                                  </span>
                                </div>
                                <p className="text-xs font-semibold text-gray-900 mt-0.5 truncate">{b.titel}</p>
                                <p className="text-[11px] text-gray-400 line-clamp-1">{b.body}</p>
                                <div className="flex items-center gap-1 mt-1 opacity-0 group-hover/bn:opacity-100 transition-opacity">
                                  <span className="text-[10px] text-gray-300">Snooze:</span>
                                  {(['1h', '1d', '1w'] as const).map(d => (
                                    <button
                                      key={d}
                                      onClick={e => { e.stopPropagation(); bnach.snooze(b.id, d); }}
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-500 font-medium"
                                    >
                                      {d === '1h' ? '1 Std' : d === '1d' ? '1 Tag' : '1 Wo'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${css.dot}`} />
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {notif.notifications.length > 0 && (
                    <div>
                      {bnach.sichtbar.length > 0 && (
                        <div className="px-4 pt-2.5 pb-1">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Nachrichten & Aufgaben</p>
                        </div>
                      )}
                      {notif.notifications.slice(0, 20).map(n => {
                        const now = Date.now();
                        const created = new Date(n.created_at).getTime();
                        const diffMin = Math.floor((now - created) / 60000);
                        const timeAgo = diffMin < 1 ? 'Gerade eben'
                          : diffMin < 60 ? `vor ${diffMin} Min`
                          : diffMin < 1440 ? `vor ${Math.floor(diffMin / 60)} Std`
                          : diffMin < 10080 ? `vor ${Math.floor(diffMin / 1440)} Tagen`
                          : new Date(n.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                        const NotifIcon = n.type === 'chat' ? MessageSquare
                          : n.type === 'aufgabe' ? CheckSquare
                          : n.type === 'termin' ? Calendar
                          : n.type === 'warnung' ? AlertCircle
                          : Bell;
                        const iconBg = n.type === 'chat' ? 'bg-blue-100 text-blue-600'
                          : n.type === 'aufgabe' ? 'bg-emerald-100 text-emerald-600'
                          : n.type === 'termin' ? 'bg-amber-100 text-amber-600'
                          : n.type === 'warnung' ? 'bg-red-100 text-red-600'
                          : 'bg-gray-100 text-gray-500';
                        return (
                          <div
                            key={n.id}
                            className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 group/notif cursor-pointer ${!n.is_read ? 'bg-blue-50/40' : ''}`}
                            onClick={() => {
                              notif.markAsRead(n.id);
                              if (n.link) { setLocation(n.link); setShowNotifications(false); }
                            }}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${iconBg}`}>
                              <NotifIcon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`text-xs truncate flex-1 ${!n.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                                  {n.title}
                                </p>
                                <span className="text-[10px] text-gray-300 flex-shrink-0 flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5" />{timeAgo}
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-400 line-clamp-2 mt-0.5">{n.message}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                              {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#9B1C1C]" />}
                              <button
                                onClick={e => { e.stopPropagation(); notif.deleteNotification(n.id); }}
                                className="p-1 rounded hover:bg-gray-200 text-gray-300 opacity-0 group-hover/notif:opacity-100 transition-opacity"
                                title="Loeschen"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {notif.notifications.length === 0 && bnach.sichtbar.length === 0 && (
                    <div className="px-4 py-10 text-center">
                      <Bell className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-400">Keine Benachrichtigungen</p>
                      <p className="text-xs text-gray-300 mt-1">Neue Hinweise und Aufgaben erscheinen hier</p>
                    </div>
                  )}
                </div>

                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between">
                  <button
                    onClick={() => { setShowNotifications(false); setLocation('/benachrichtigungen'); }}
                    className="text-xs font-medium text-[#9B1C1C] hover:underline"
                  >
                    Alle Benachrichtigungen
                  </button>
                  <button
                    onClick={() => { setShowNotifications(false); setLocation('/kommunikation'); }}
                    className="text-xs font-medium text-gray-500 hover:underline"
                  >
                    Kommunikation
                  </button>
                </div>
              </div>
            )}
          </div>

          {isAdmin && (
            <div ref={impersonateRef} className="relative hidden sm:block">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => { setShowImpersonate(!showImpersonate); setShowNotifications(false); setShowUserMenu(false); setShowNeueAkte(false); }}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                      impersonateUserId
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title="Mitarbeiter-Ansicht"
                  >
                    {impersonateUserId ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  {impersonateUserId ? 'Ansichtswechsel aktiv - klicken zum Beenden' : 'Mitarbeiter-Ansicht simulieren'}
                </TooltipContent>
              </Tooltip>

              {showImpersonate && (
                <div className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] sm:w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Ansicht wechseln</p>
                      <p className="text-[10px] text-gray-400">Sehe was dein Mitarbeiter sieht</p>
                    </div>
                    {impersonateUserId && (
                      <button
                        onClick={() => { setImpersonateUserId(null); setShowImpersonate(false); toast.success('Zurueck zur Admin-Ansicht'); }}
                        className="text-[10px] font-medium px-2 py-1 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200"
                      >
                        Beenden
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    <button
                      onClick={() => { setImpersonateUserId(null); setShowImpersonate(false); toast.success('Admin-Ansicht aktiv'); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left ${
                        !impersonateUserId ? 'bg-green-50' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#B91C1C] text-white flex items-center justify-center text-[10px] font-bold">
                        {profile?.initialen || 'AK'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-800 truncate">{profile?.name} (Du)</p>
                        <p className="text-[10px] text-gray-400">Administrator - Alle Module</p>
                      </div>
                      {!impersonateUserId && <span className="text-[10px] text-green-600 font-medium">Aktiv</span>}
                    </button>

                    <div className="h-px bg-gray-100 my-1" />

                    {teamMembers
                      .filter(m => m.id !== profile?.id && m.ist_aktiv && !isKiAgent(m.id) && m.rolle !== 'ki_agent')
                      .map(m => {
                        const init = `${m.vorname[0] || ''}${m.nachname[0] || ''}`.toUpperCase();
                        const isActive = impersonateUserId === m.id;
                        const activeModules = m.module_permissions
                          ? Object.entries(m.module_permissions).filter(([_, v]) => v).map(([k]) => k)
                          : m.rolle === 'admin' ? ['Alle'] : ['aufgaben', 'kommunikation'];
                        return (
                          <button
                            key={m.id}
                            onClick={() => {
                              setImpersonateUserId(m.id);
                              setShowImpersonate(false);
                              toast.success(`Ansicht: ${m.vorname} ${m.nachname}`, {
                                description: `Rolle: ${m.rolle} - ${activeModules.length} Module aktiv`,
                              });
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left ${
                              isActive ? 'bg-amber-50' : ''
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-bold">
                              {init}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate">{m.vorname} {m.nachname}</p>
                              <p className="text-[10px] text-gray-400 truncate">
                                {m.rolle} - {activeModules.slice(0, 3).join(', ')}{activeModules.length > 3 ? ` +${activeModules.length - 3}` : ''}
                              </p>
                            </div>
                            {isActive && <span className="text-[10px] text-amber-600 font-medium">Aktiv</span>}
                            {m.is_online && <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          <MitarbeiterMenu />

          {effectiveIsAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => { setShowAgentenZentrale(true); setShowUserMenu(false); setShowNotifications(false); }}
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors group relative"
                  title="Kommandozentrale oeffnen"
                >
                  <Bot className="w-[18px] h-[18px] text-[#9B1C1C] group-hover:text-[#7F1D1D] transition-colors" />
                  <span className="absolute top-0.5 left-[22px] sm:hidden w-2 h-2 bg-[#C41E3A] rounded-full animate-pulse" />
                  <div className="text-left hidden sm:block">
                    <p className="text-xs font-bold text-gray-900 leading-none">{effectiveProfile?.name || 'Andreas Koop'}</p>
                    <p className="text-[9px] text-[#9B1C1C] font-semibold leading-none mt-0.5">Kommandozentrale</p>
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}>
                Kommandozentrale oeffnen
              </TooltipContent>
            </Tooltip>
          )}

          <div ref={userRef} className="relative">
            <button
              onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); setShowNeueAkte(false); }}
              className="flex items-center gap-1.5 rounded-lg hover:bg-gray-100 transition-colors px-2 py-1.5"
            >
              <div className="w-8 h-8 rounded-full bg-[#B91C1C] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {effectiveProfile?.initialen || 'AK'}
              </div>
              <ChevronDown className="w-3 h-3 text-gray-400 hidden sm:block" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">{effectiveProfile?.name || 'Benutzer'}</p>
                  <p className="text-[11px] text-gray-400">{effectiveProfile?.rolle === 'admin' ? 'Administrator' : effectiveProfile?.rolle === 'sachbearbeiter' ? 'Sachbearbeiter' : 'Mitarbeiter'}</p>
                </div>
                <button
                  onClick={() => { setLocation('/einstellungen'); setShowUserMenu(false); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Einstellungen
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); closeAkte(); sessionStorage.setItem('kiosk_access', 'true'); window.location.href = '/kiosk/werkstatt'; }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                >
                  <Wrench className="w-4 h-4" />
                  Werkstatt-Kiosk
                </button>
                <button
                  onClick={async () => {
                    setShowUserMenu(false);
                    await signOut();
                    setLocation('/login');
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {effectiveIsAdmin && (
        <AgentenKommandozentrale open={showAgentenZentrale} onOpenChange={setShowAgentenZentrale} />
      )}

      {activeAkteType && (
        <NeueAkteDialog
          type={activeAkteType}
          onClose={() => setActiveAkteType(null)}
          onSuccess={handleAkteSuccess}
        />
      )}
    </>
  );
}
